import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import CustomCalendar from './CustomCalendar';
import EventPopup from './EventPopup';
import HoverSidebar from './HoverSidebar';
import api/*, { searchUsers, grantManagerAccess }*/ from '../utils/api';
import { expandAllRecurringEvents } from '../utils/recurrence';
import '../styles/Dashboard.css';
import '../styles/theme.css';

const Dashboard = () => {
    const [employees, setEmployees] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [currentDate, setCurrentDate] = useState(moment());
    const [schedules, setSchedules] = useState([]);
    const [viewMode, setViewMode] = useState('day');
    const [activeTab, setActiveTab] = useState('dashboard');
    const [searchTerm, setSearchTerm] = useState('');
    const [taskSearchTerm, setTaskSearchTerm] = useState('');
    const [selectedMode, setSelectedMode] = useState('calendar');
    
    // Add sort state
    const [sortOrder, setSortOrder] = useState(null); // null, 'highest', 'lowest'
    const [showSortOptions, setShowSortOptions] = useState(false);
    
    // Add filter state
    const [showFilterOptions, setShowFilterOptions] = useState(false);
    
    // Add department visibility state - Initialize with all departments visible
    const [visibleDepartments, setVisibleDepartments] = useState(() => {
        // Start with all departments visible by default
        return new Set();
    });
    
    // EventPopup state variables
    const [isEventPopupOpen, setIsEventPopupOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedStartTime, setSelectedStartTime] = useState('');
    const [selectedEndTime, setSelectedEndTime] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [existingEvent, setExistingEvent] = useState(null);
    const [prefilledParticipants, setPrefilledParticipants] = useState([]);
    
    // Add Manager Modal State - COMMENTED OUT
    /*
    const [showAddManagerModal, setShowAddManagerModal] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [grantingAccess, setGrantingAccess] = useState(null); // ID of user being granted access
    */

    // Simple navigation function
    const navigateTo = (path) => {
        window.history.pushState({}, '', path);
        window.location.href = path;
    };

    const getRandomPastelColor = (seed) => {
        const colors = [
            '#2196F3', // blue
            '#9C27B0', // purple
            '#4CAF50', // green
            '#FF9800', // orange
            '#E91E63', // pink
            '#00BCD4', // cyan
            '#009688', // teal
            '#607D8B'  // blue gray
        ];
        const index = Math.abs(seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colors.length;
        return colors[index];
    };

    const renderAvatar = (employee) => {
        const firstName = employee.first_name || '';
        const lastName = employee.last_name || '';
        const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 
                        employee.email?.charAt(0)?.toUpperCase() || 'U';
        const backgroundColor = getRandomPastelColor(employee.email);
        const profilePicture = employee.profile_picture_url;
        
        if (profilePicture) {
            return (
                <div 
                    key={employee.id} 
                    className="avatar" 
                    title={employee.email}
                    style={{ backgroundColor: 'transparent', overflow: 'hidden', position: 'relative' }}
                >
                    <img 
                        src={profilePicture} 
                        alt={`${firstName} ${lastName}`} 
                        style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '50%'
                        }}
                        onError={(e) => {
                            console.error('Avatar image failed to load:', profilePicture);
                            // Hide the image and show initials fallback
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling;
                            if (fallback) {
                                fallback.style.display = 'flex';
                            }
                        }}
                    />
                    <div 
                        className="avatar" 
                        style={{ 
                            backgroundColor,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            display: 'none'
                        }}
                    >
                        {initials}
                    </div>
                </div>
            );
        }
        
        return initials ? (
            <div 
                key={employee.id} 
                className="avatar" 
                title={employee.email}
                style={{ backgroundColor }}
            >
                {initials}
            </div>
        ) : null;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setError('No authentication token found');
                    setLoading(false);
                    return;
                }

                // Fetch employees, company info, schedules, and requests in parallel using api utility
                const [employeesResponse, companyResponse, schedulesResponse, requestsResponse] = await Promise.all([
                    api.get('/api/users/employees'),
                    api.get('/api/companies/info'),
                    api.get('/api/schedules'),
                    api.get('/api/requests/company')
                ]);

                console.log('Employees:', employeesResponse.data);
                console.log('Company:', companyResponse.data);
                console.log('Raw schedules data:', schedulesResponse.data);
                console.log('Requests data:', requestsResponse.data);
                
                // Debug employee roles specifically
                if (employeesResponse.data && employeesResponse.data.employees) {
                    const employeeRoles = employeesResponse.data.employees.map(emp => ({
                        name: `${emp.first_name} ${emp.last_name}`,
                        email: emp.email,
                        role: emp.role
                    }));
                    console.log('🔍 Employee roles breakdown:', employeeRoles);
                    
                    const uniqueRoles = [...new Set(employeesResponse.data.employees.map(emp => emp.role).filter(Boolean))];
                    console.log('🔍 All unique roles from backend:', uniqueRoles);
                }
                
                // Debug: Check if any schedules have zoom_info
                const schedulesWithZoom = schedulesResponse.data.filter(schedule => schedule.zoom_info);
                console.log('Schedules with zoom_info:', schedulesWithZoom);
                
                // Create a mapping between user_ids and employee IDs
                const userIdToEmployeeId = {};
                
                employeesResponse.data.employees.forEach(employee => {
                    if (employee.user_id) {
                        userIdToEmployeeId[employee.user_id] = employee.id;
                    }
                });

                // Convert schedules to calendar format
                const formattedSchedules = schedulesResponse.data.map(schedule => {
                    const mappedEmployeeId = userIdToEmployeeId[schedule.user_id];
                    
                    // Debug: Log schedule with zoom_info
                    if (schedule.zoom_info) {
                        console.log('🎯 Schedule with zoom_info found:', {
                            id: schedule.id,
                            title: schedule.title,
                            zoom_info: schedule.zoom_info
                        });
                    }
                    
                    // Parse the shift_date to get just the date part
                    const shiftDate = schedule.shift_date?.split('T')[0];
                    if (!shiftDate || !schedule.start_time || !schedule.end_time) {
                        console.warn('Invalid schedule data:', schedule);
                        return null;
                    }

                    const startTime = moment(`${shiftDate} ${schedule.start_time}`);
                    const endTime = moment(`${shiftDate} ${schedule.end_time}`);
                    
                    if (!startTime.isValid() || !endTime.isValid()) {
                        console.warn('Invalid date/time in schedule:', schedule);
                        return null;
                    }

                    // Debug logging for all-day events
                    if (schedule.is_all_day) {
                        console.log('🐛 Dashboard transforming all-day schedule:', {
                            id: schedule.id,
                            title: schedule.title,
                            is_all_day: schedule.is_all_day,
                            start: schedule.start,
                            end: schedule.end
                        });
                    }

                    return {
                        id: schedule.id,
                        calendarId: schedule.calendar_type || 'tasks',
                        title: schedule.title || 'Untitled Schedule',
                        start: startTime.toDate(),
                        end: endTime.toDate(),
                        resource: mappedEmployeeId ? String(mappedEmployeeId) : undefined,
                        color: schedule.color || '#4b8dff',
                        backgroundColor: schedule.color || '#4b8dff',
                        files: schedule.files || [],
                        description: schedule.description || '',
                        location: schedule.location || '',
                        isAllDay: schedule.is_all_day || false,
                        is_all_day: schedule.is_all_day || false, // Add both property names for compatibility
                        zoom_info: schedule.zoom_info || null,
                        recurrence_rule: schedule.recurrence_rule || null,
                        raw: {
                            participants: schedule.participants || []
                        }
                    };
                }).filter(Boolean); // Remove any null entries

                // Expand recurring events to show all instances
                const expandedSchedules = expandAllRecurringEvents(formattedSchedules);

                // Convert approved requests to calendar events
                const approvedRequests = requestsResponse.data.filter(request => request.status === 'approved');
                console.log('Approved requests:', approvedRequests);

                const requestEvents = [];
                approvedRequests.forEach(request => {
                    const mappedEmployeeId = userIdToEmployeeId[request.user_id];
                    if (!mappedEmployeeId) {
                        console.warn('No employee mapping found for user_id:', request.user_id);
                        return;
                    }

                    // Generate events for each day in the request range
                    const startDate = moment(request.start_date);
                    const endDate = moment(request.end_date);
                    const currentDate = startDate.clone();

                    while (currentDate.isSameOrBefore(endDate, 'day')) {
                        // Create PTO event as 8-hour block (9am-5pm)
                        const eventStart = currentDate.clone().hour(9).minute(0).second(0);
                        const eventEnd = currentDate.clone().hour(17).minute(0).second(0);

                        // Determine title and color based on PTO status
                        let title, color, backgroundColor;
                        if (request.is_pto) {
                            title = `PTO - ${request.request_type.replace('_', ' ')}`;
                            color = '#10B981'; // Green for PTO
                            backgroundColor = '#10B981';
                        } else {
                            title = `Time Off - ${request.request_type.replace('_', ' ')}`;
                            color = '#F59E0B'; // Orange for regular time off
                            backgroundColor = '#F59E0B';
                        }

                        requestEvents.push({
                            id: `request-${request.id}-${currentDate.format('YYYY-MM-DD')}`,
                            calendarId: 'time-off',
                            title: title,
                            start: eventStart.toDate(),
                            end: eventEnd.toDate(),
                            resource: String(mappedEmployeeId),
                            color: color,
                            backgroundColor: backgroundColor,
                            description: request.reason || '',
                            isAllDay: false,
                            isPTO: request.is_pto || false,
                            requestType: request.request_type,
                            requestId: request.id,
                            countsTowardHours: request.is_pto || false,
                            raw: {
                                participants: []
                            }
                        });

                        currentDate.add(1, 'day');
                    }
                });

                // Combine schedules and request events
                const allEvents = [...expandedSchedules, ...requestEvents];
                console.log('All formatted events:', allEvents);
                setSchedules(allEvents);

                if (!employeesResponse.data || !Array.isArray(employeesResponse.data.employees)) {
                    throw new Error('Invalid employees data received');
                }

                console.log('Employees response:', employeesResponse.data.employees);
                setEmployees(employeesResponse.data.employees);
                
                if (!companyResponse.data) {
                    throw new Error('Invalid company data received');
                }
                
                console.log('Company info response:', companyResponse.data);
                setCompanyInfo(companyResponse.data);

            } catch (error) {
                console.error('Error fetching data:', error);
                if (error.response) {
                    console.error('Error response:', error.response.data);
                    if (error.response.status === 401) {
                        localStorage.removeItem('token'); // Clear invalid token
                        setError('Session expired. Please log in again.');
                    } else {
                        setError(error.response.data?.message || error.response.data?.error || 'Failed to fetch data');
                    }
                } else if (error.request) {
                    console.error('No response received:', error.request);
                    setError('Network error. Please check your connection.');
                } else {
                    console.error('Error details:', error);
                    setError(error.message || 'An unexpected error occurred');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleScheduleCreate = async (eventData, formData = null) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Authentication token missing');
            }

            // If formData is provided, we're handling file uploads
            if (formData) {
                console.log('📎 Creating schedule with file uploads');
                
                // Use the regular /api/schedules endpoint with FormData
                const response = await api.post('/api/schedules', formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                console.log('Schedule with files created successfully:', response.data);
                
                // Add the new schedule to the state with proper time handling
                const createdSchedule = {
                    id: response.data.id,
                    calendarId: response.data.type || 'tasks',
                    title: response.data.title,
                    start: moment(`${response.data.shift_date} ${response.data.start_time}`).toDate(),
                    end: moment(`${response.data.shift_date} ${response.data.end_time}`).toDate(),
                    resource: String(eventData.resource),
                    description: response.data.description,
                    location: response.data.location,
                    isAllDay: response.data.is_all_day,
                    is_all_day: response.data.is_all_day, // Add both property names for compatibility
                    color: response.data.color || '#4b8dff',
                    backgroundColor: response.data.color || '#4b8dff',
                    files: response.data.files || [], // Include files in the schedule
                    zoom_info: response.data.zoom_info || null,
                    raw: {
                        participants: response.data.participants || eventData.participants || []
                    }
                };

                setSchedules(prev => [...prev, createdSchedule]);
                return response.data.id;
            }

            // Regular schedule creation without files
            // Validate required fields
            if (!eventData.user_id || !eventData.company_id || !eventData.shift_date || !eventData.start_time || !eventData.end_time) {
                console.error('Missing required fields:', eventData);
                throw new Error('Missing required fields for schedule creation');
            }

            // Validate date formats
            if (!moment(eventData.shift_date, 'YYYY-MM-DD', true).isValid()) {
                throw new Error('Invalid shift date format');
            }
            if (!moment(eventData.start_time, 'HH:mm:ss', true).isValid()) {
                throw new Error('Invalid start time format');
            }
            if (!moment(eventData.end_time, 'HH:mm:ss', true).isValid()) {
                throw new Error('Invalid end time format');
            }

            console.log('Creating new schedule:', eventData);

            // Create schedule object with required fields
            const newSchedule = {
                user_id: eventData.user_id,
                company_id: eventData.company_id,
                shift_date: eventData.shift_date,
                start_time: eventData.start_time,
                end_time: eventData.end_time,
                title: eventData.title || 'Work Hours',
                type: eventData.type || 'shift',
                description: eventData.description || '',
                is_all_day: eventData.is_all_day || false,
                location: eventData.location || '',
                status: eventData.status || 'confirmed',
                color: eventData.color || '#4b8dff',
                organizer: eventData.organizer || '',
                source: eventData.source || 'optivo',
                calendar: eventData.calendar || 'Optivo',
                zoom_info: eventData.zoom_info || null,
                participants: eventData.participants || []
            };

            console.log('Sending schedule creation request:', newSchedule);
            
            const response = await api.post('/api/schedules', newSchedule);
            console.log('Schedule created successfully:', response.data);

            // Add the new schedule to the state with proper time handling
            const createdSchedule = {
                id: response.data.id,
                calendarId: response.data.type || 'tasks',
                title: response.data.title,
                start: moment(`${response.data.shift_date} ${response.data.start_time}`).toDate(),
                end: moment(`${response.data.shift_date} ${response.data.end_time}`).toDate(),
                resource: String(eventData.resource),
                description: response.data.description,
                location: response.data.location,
                isAllDay: response.data.is_all_day,
                is_all_day: response.data.is_all_day, // Add both property names for compatibility
                color: response.data.color || '#4b8dff',
                backgroundColor: response.data.color || '#4b8dff',
                zoom_info: response.data.zoom_info || null,
                raw: {
                    participants: response.data.participants || eventData.participants || []
                }
            };

            setSchedules(prev => [...prev, createdSchedule]);
            return response.data.id;
        } catch (error) {
            console.error('Error creating schedule:', {
                error: error.message,
                response: error.response?.data,
                status: error.response?.status,
                fullError: error
            });
            
            let errorMessage = 'Failed to create schedule';
            if (error.response?.data?.errors) {
                errorMessage = error.response.data.errors.join(', ');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            throw new Error(errorMessage);
        }
    };

    const renderMiniCalendar = () => {
        const startDay = currentMonth.clone().startOf('month').startOf('week');
        const endDay = currentMonth.clone().endOf('month').endOf('week');
        const calendar = [];
        const day = startDay.clone();

        while (day.isSameOrBefore(endDay, 'day')) {
            calendar.push(day.clone());
            day.add(1, 'day');
        }

        return (
            <div style={styles.miniCalendar}>
                <div style={styles.miniCalendarHeader}>
                    <div style={styles.miniCalendarMonthYearHeader}>
                        <div style={{ display: 'flex', alignItems: 'baseline' }}>
                            <span style={styles.monthText}>{currentMonth.format('MMMM')}</span>
                            <span style={styles.yearText}>{currentMonth.format('YYYY')}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button 
                                className="mini-calendar-nav-btn"
                                type="button"
                                onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M15.2484 6.35159C15.4734 6.57662 15.5998 6.88179 15.5998 7.19999C15.5998 7.51818 15.4734 7.82335 15.2484 8.04839L11.2968 12L15.2484 15.9516C15.467 16.1779 15.588 16.481 15.5852 16.7957C15.5825 17.1103 15.4563 17.4113 15.2338 17.6338C15.0113 17.8563 14.7103 17.9825 14.3957 17.9852C14.0811 17.9879 13.7779 17.867 13.5516 17.6484L8.75161 12.8484C8.52665 12.6234 8.40027 12.3182 8.40027 12C8.40027 11.6818 8.52665 11.3766 8.75161 11.1516L13.5516 6.35159C13.7766 6.12662 14.0818 6.00024 14.4 6.00024C14.7182 6.00024 15.0234 6.12662 15.2484 6.35159Z" fill="currentColor"/>
                                </svg>
                            </button>
                            <button 
                                className="mini-calendar-nav-btn"
                                type="button"
                                onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'month'))}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scaleX(-1)' }}>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M15.2484 6.35159C15.4734 6.57662 15.5998 6.88179 15.5998 7.19999C15.5998 7.51818 15.4734 7.82335 15.2484 8.04839L11.2968 12L15.2484 15.9516C15.467 16.1779 15.588 16.481 15.5852 16.7957C15.5825 17.1103 15.4563 17.4113 15.2338 17.6338C15.0113 17.8563 14.7103 17.9825 14.3957 17.9852C14.0811 17.9879 13.7779 17.867 13.5516 17.6484L8.75161 12.8484C8.52665 12.6234 8.40027 12.3182 8.40027 12C8.40027 11.6818 8.52665 11.3766 8.75161 11.1516L13.5516 6.35159C13.7766 6.12662 14.0818 6.00024 14.4 6.00024C14.7182 6.00024 15.0234 6.12662 15.2484 6.35159Z" fill="currentColor"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div style={styles.miniCalendarDayNames}>
                        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                            <div key={day} style={styles.dayName}>{day}</div>
                        ))}
                    </div>
                </div>
                <div style={styles.miniCalendarDays}>
                    {calendar.map(date => (
                        <div
                            key={date.format('YYYY-MM-DD')}
                            style={{
                                ...styles.calendarDay,
                                ...(date.isSame(moment(), 'day') && styles.today),
                                ...(date.month() !== currentMonth.month() && styles.otherMonth)
                            }}
                            onClick={() => {
                                // Navigate to the clicked date
                                setCurrentDate(date.clone());
                                console.log('Mini calendar date clicked:', date.format('YYYY-MM-DD'));
                            }}
                            onMouseEnter={(e) => {
                                if (!date.isSame(moment(), 'day')) {
                                    e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                                    e.target.style.transform = 'scale(1.05)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!date.isSame(moment(), 'day')) {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.transform = 'scale(1)';
                                }
                            }}
                        >
                            {date.format('D')}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleTabClick = (tab, event) => {
        if (event) {
            event.preventDefault();
        }
        setActiveTab(tab);
        // Add navigation logic here when we create the Employees page
        if (tab === 'dashboard') {
            navigateTo('/dashboard');
        } else if (tab === 'employees') {
            navigateTo('/employees');
        } else if (tab === 'requests') {
            navigateTo('/requests');
        }
    };

    const handleNavigation = (route) => {
        navigateTo(route);
    };

    // Add function to handle view toggle
    const handleViewToggle = () => {
        // Get the token from localStorage
        const token = localStorage.getItem('token') || localStorage.getItem('employeeToken');
        
        if (token) {
            // Store the token in both localStorage and sessionStorage for the employee app
            localStorage.setItem('employeeToken', token);
            sessionStorage.setItem('employeeToken', token);
            
            // Navigate to the employee calendar
            window.location.href = 'https://calendar.joinoptivo.com';
        } else {
            console.error('No token found for navigation');
        }
    };

    // Add refreshSchedules function
    const refreshSchedules = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('No token found for refresh');
            return;
        }

        try {
            console.log('🔄 Refreshing schedules and requests...');
            
            const [schedulesResponse, requestsResponse] = await Promise.all([
                api.get('/api/schedules'),
                api.get('/api/requests/company')
            ]);
            
            if (!schedulesResponse.data || !Array.isArray(schedulesResponse.data)) {
                throw new Error('Invalid schedules data received during refresh');
            }

            // Create a mapping between user_ids and employee IDs
            const userIdToEmployeeId = {};
            employees.forEach(employee => {
                if (employee.user_id) {
                    userIdToEmployeeId[employee.user_id] = employee.id;
                }
            });

            // Convert schedules to calendar format
            const formattedSchedules = schedulesResponse.data.map(schedule => {
                const mappedEmployeeId = userIdToEmployeeId[schedule.user_id];
                
                const shiftDate = schedule.shift_date?.split('T')[0];
                if (!shiftDate || !schedule.start_time || !schedule.end_time) {
                    console.warn('Invalid schedule data:', schedule);
                    return null;
                }

                const startTime = moment(`${shiftDate} ${schedule.start_time}`);
                const endTime = moment(`${shiftDate} ${schedule.end_time}`);
                
                if (!startTime.isValid() || !endTime.isValid()) {
                    console.warn('Invalid date/time in schedule:', schedule);
                    return null;
                }

                // Debug logging for all-day events
                if (schedule.is_all_day) {
                    console.log('🐛 Dashboard transforming all-day schedule:', {
                        id: schedule.id,
                        title: schedule.title,
                        is_all_day: schedule.is_all_day,
                        start: schedule.start,
                        end: schedule.end
                    });
                }

                return {
                    id: schedule.id,
                    calendarId: schedule.calendar_type || 'tasks',
                    title: schedule.title || 'Untitled Schedule',
                    start: startTime.toDate(),
                    end: endTime.toDate(),
                    resource: mappedEmployeeId ? String(mappedEmployeeId) : undefined,
                    color: schedule.color || '#4b8dff',
                    backgroundColor: schedule.color || '#4b8dff',
                    files: schedule.files || [],
                    description: schedule.description || '',
                    location: schedule.location || '',
                    isAllDay: schedule.is_all_day || false,
                    is_all_day: schedule.is_all_day || false, // Add both property names for compatibility
                    zoom_info: schedule.zoom_info || null,
                    recurrence_rule: schedule.recurrence_rule || null,
                    raw: {
                        participants: schedule.participants || []
                    }
                };
            }).filter(Boolean);

            // Expand recurring events to show all instances
            console.log('🔄 Schedules before expansion:', formattedSchedules.length, 'events');
            console.log('🔄 Recurring events found:', formattedSchedules.filter(s => s.recurrence_rule).map(s => ({
                id: s.id,
                title: s.title,
                recurrence_rule: s.recurrence_rule,
                start: s.start
            })));
            
            const expandedSchedules = expandAllRecurringEvents(formattedSchedules);
            
            // Fix resource field for recurring instances to use proper employee ID mapping
            const fixedExpandedSchedules = expandedSchedules.map(schedule => {
                if (schedule.isRecurringInstance) {
                    // Find the original schedule data to get the user_id
                    const originalSchedule = schedulesResponse.data.find(s => s.id === schedule.originalEventId);
                    if (originalSchedule) {
                        const mappedEmployeeId = userIdToEmployeeId[originalSchedule.user_id];
                        return {
                            ...schedule,
                            resource: mappedEmployeeId ? String(mappedEmployeeId) : schedule.resource
                        };
                    }
                }
                return schedule;
            });
            
            console.log('🔄 Schedules after expansion and resource fix:', fixedExpandedSchedules.length, 'events');
            console.log('🔄 Recurring instances found:', fixedExpandedSchedules.filter(s => s.isRecurringInstance).map(s => ({
                id: s.id,
                title: s.title,
                instanceDate: s.instanceDate,
                start: s.start,
                resource: s.resource
            })));

            // Convert approved requests to calendar events
            const approvedRequests = requestsResponse.data.filter(request => request.status === 'approved');
            console.log('🔄 Approved requests during refresh:', approvedRequests);

            const requestEvents = [];
            approvedRequests.forEach(request => {
                const mappedEmployeeId = userIdToEmployeeId[request.user_id];
                if (!mappedEmployeeId) {
                    console.warn('No employee mapping found for user_id:', request.user_id);
                    return;
                }

                // Generate events for each day in the request range
                const startDate = moment(request.start_date);
                const endDate = moment(request.end_date);
                const currentDate = startDate.clone();

                while (currentDate.isSameOrBefore(endDate, 'day')) {
                    // Create PTO event as 8-hour block (9am-5pm)
                    const eventStart = currentDate.clone().hour(9).minute(0).second(0);
                    const eventEnd = currentDate.clone().hour(17).minute(0).second(0);

                    // Determine title and color based on PTO status
                    let title, color, backgroundColor;
                    if (request.is_pto) {
                        title = `PTO - ${request.request_type.replace('_', ' ')}`;
                        color = '#10B981'; // Green for PTO
                        backgroundColor = '#10B981';
                    } else {
                        title = `Time Off - ${request.request_type.replace('_', ' ')}`;
                        color = '#F59E0B'; // Orange for regular time off
                        backgroundColor = '#F59E0B';
                    }

                    requestEvents.push({
                        id: `request-${request.id}-${currentDate.format('YYYY-MM-DD')}`,
                        calendarId: 'time-off',
                        title: title,
                        start: eventStart.toDate(),
                        end: eventEnd.toDate(),
                        resource: String(mappedEmployeeId),
                        color: color,
                        backgroundColor: backgroundColor,
                        description: request.reason || '',
                        isAllDay: false,
                        isPTO: request.is_pto || false,
                        requestType: request.request_type,
                        requestId: request.id,
                        countsTowardHours: request.is_pto || false,
                        raw: {
                            participants: []
                        }
                    });

                    currentDate.add(1, 'day');
                }
            });

            // Combine schedules and request events
            const allEvents = [...fixedExpandedSchedules, ...requestEvents];
            console.log('🔄 Schedules and requests refreshed:', allEvents.length, 'total events found');
            setSchedules(allEvents);
        } catch (error) {
            console.error('Error refreshing schedules:', error);
        }
    };

    // Day navigation functions
    const goToPreviousDay = () => {
        const amount = viewMode === 'week' ? 7 : 1;
        const unit = viewMode === 'week' ? 'week' : 'day';
        const newDate = moment(currentDate).subtract(amount, viewMode === 'week' ? 'days' : 'day');
        console.log(`Previous ${unit} clicked - navigating from:`, currentDate.format('YYYY-MM-DD'), 'to:', newDate.format('YYYY-MM-DD'));
        setCurrentDate(newDate);
    };

    const goToNextDay = () => {
        const amount = viewMode === 'week' ? 7 : 1;
        const unit = viewMode === 'week' ? 'week' : 'day';
        const newDate = moment(currentDate).add(amount, viewMode === 'week' ? 'days' : 'day');
        console.log(`Next ${unit} clicked - navigating from:`, currentDate.format('YYYY-MM-DD'), 'to:', newDate.format('YYYY-MM-DD'));
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        const today = moment();
        console.log('Today button clicked - navigating from:', currentDate.format('YYYY-MM-DD'), 'to today:', today.format('YYYY-MM-DD'));
        setCurrentDate(today);
    };

    // Filter employees based on search term
    const filteredEmployees = employees.filter(employee => {
        if (!searchTerm.trim()) return true;
        
        // Split search term by commas and trim each part
        const searchTerms = searchTerm.split(',').map(term => term.trim().toLowerCase()).filter(term => term.length > 0);
        
        // If no valid search terms after splitting, return true
        if (searchTerms.length === 0) return true;
        
        const firstName = employee.first_name?.toLowerCase() || '';
        const lastName = employee.last_name?.toLowerCase() || '';
        const email = employee.email?.toLowerCase() || '';
        const role = employee.role?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        // Check if employee matches any of the search terms
        return searchTerms.some(searchLower => {
            return firstName.includes(searchLower) ||
                   lastName.includes(searchLower) ||
                   email.includes(searchLower) ||
                   role.includes(searchLower) ||
                   fullName.includes(searchLower);
        });
    });

    // Filter tasks/schedules based on task search term
    const filteredSchedules = schedules.filter(schedule => {
        if (!taskSearchTerm.trim()) return schedule;
        
        const searchLower = taskSearchTerm.toLowerCase();
        const title = schedule.title?.toLowerCase() || '';
        const description = schedule.description?.toLowerCase() || '';
        const location = schedule.location?.toLowerCase() || '';
        
        // Also search by employee name if we can find the employee
        let employeeName = '';
        if (schedule.resource) {
            const employee = employees.find(emp => emp.id === parseInt(schedule.resource));
            if (employee) {
                const firstName = employee.first_name?.toLowerCase() || '';
                const lastName = employee.last_name?.toLowerCase() || '';
                employeeName = `${firstName} ${lastName}`.trim();
            }
        }
        
        return title.includes(searchLower) ||
               description.includes(searchLower) ||
               location.includes(searchLower) ||
               employeeName.includes(searchLower);
    });

    // Calculate weekly hours for an employee
    const calculateWeeklyHours = (employeeId) => {
        const startOfWeek = currentDate.clone().startOf('week');
        const endOfWeek = currentDate.clone().endOf('week');
        
        const weeklySchedules = schedules.filter(schedule => {
            const scheduleStart = moment(schedule.start);
            return schedule.resource === String(employeeId) && 
                   scheduleStart.isBetween(startOfWeek, endOfWeek, 'day', '[]');
        });

        console.log(`🐛 Dashboard calculateWeeklyHours for employee ${employeeId}:`, weeklySchedules.map(s => ({
            id: s.id,
            title: s.title,
            isAllDay: s.isAllDay,
            is_all_day: s.is_all_day,
            start: s.start,
            end: s.end
        })));
        
        return weeklySchedules.reduce((total, schedule) => {
                // Only count hours for:
                // 1. Regular work schedules (no countsTowardHours property)
                // 2. PTO requests (countsTowardHours === true)
                // Skip regular time-off requests (countsTowardHours === false)
                if (schedule.hasOwnProperty('countsTowardHours') && !schedule.countsTowardHours) {
                    return total; // Skip this event - it's regular time-off, not PTO
                }
                
                const startTime = moment(schedule.start);
                const endTime = moment(schedule.end);
                const duration = moment.duration(endTime.diff(startTime));
                
                // For all-day events, return 8 hours instead of calculating the full 24 hours
                let hours;
                if (schedule.is_all_day) {
                    hours = 8;
                    console.log(`🐛 Dashboard: All-day event detected, using 8 hours for ${schedule.title}`);
                } else {
                    hours = Math.round(duration.asHours() * 100) / 100; // Round to 2 decimal places
                    console.log(`🐛 Dashboard: Regular event, calculated ${hours} hours for ${schedule.title}`);
                }
                
                return total + hours;
            }, 0);
    };

    // Sort employees by weekly hours
    const getSortedEmployees = (employeesList) => {
        if (!sortOrder) return employeesList;
        
        return [...employeesList].sort((a, b) => {
            const hoursA = calculateWeeklyHours(a.id);
            const hoursB = calculateWeeklyHours(b.id);
            
            if (sortOrder === 'highest') {
                return hoursB - hoursA; // Descending order (highest first)
            } else if (sortOrder === 'lowest') {
                return hoursA - hoursB; // Ascending order (lowest first)
            }
            return 0;
        });
    };

    // Apply sorting to filtered employees
    const sortedAndFilteredEmployees = getSortedEmployees(filteredEmployees);

    // Function to get employee role (same logic as CustomCalendar)
    const getEmployeeRole = (employee) => {
        const savedRoles = JSON.parse(localStorage.getItem('employeeRoles') || '{}');
        return savedRoles[employee.id] || employee.role || 'Employee';
    };

    // Get unique departments/roles from employees using the same logic as CustomCalendar
    const getUniqueDepartments = () => {
        const departments = employees.map(emp => getEmployeeRole(emp)).filter(Boolean);
        const uniqueDepartments = [...new Set(departments)].sort();
        console.log('🏢 All departments from employees (with localStorage):', departments);
        console.log('🏢 Unique departments (with localStorage):', uniqueDepartments);
        return uniqueDepartments;
    };

    // Function to get role color (same logic as CustomCalendar)
    const getRoleColor = (role) => {
        // Get saved colors from localStorage (same as CustomCalendar)
        const savedColors = JSON.parse(localStorage.getItem('roleColors') || '{}');
        if (savedColors[role]) {
            console.log('🎨 Role color from localStorage:', role, savedColors[role]);
            return savedColors[role];
        }

        // Get all unique roles from employees to ensure consistent coloring
        const allRoles = [...new Set(employees.map(emp => getEmployeeRole(emp)).filter(Boolean))].sort();
        
        // Debug logging to see what roles we actually have
        console.log('🎨 All unique roles found:', allRoles);
        console.log('🎨 Looking for color for role:', role);
        
        // Color palette for roles (same as CustomCalendar)
        const roleColors = [
            '#4CAF50', // Green
            '#2196F3', // Blue
            '#FF9800', // Orange
            '#9C27B0', // Purple
            '#E91E63', // Pink
            '#00BCD4', // Cyan
            '#FF5722', // Red
            '#607D8B', // Blue Gray
            '#795548', // Brown
            '#FFC107', // Amber
        ];
        
        // Find the index of this role in the sorted array
        const roleIndex = allRoles.indexOf(role);
        
        console.log('🎨 Role index:', roleIndex, 'Color:', roleIndex !== -1 ? roleColors[roleIndex % roleColors.length] : '#4CAF50');
        
        // Return color based on index, cycling through the palette if we have more roles than colors
        return roleIndex !== -1 ? roleColors[roleIndex % roleColors.length] : '#4CAF50';
    };

    // Toggle department visibility
    const toggleDepartmentVisibility = (department) => {
        console.log('🔍 Toggling department visibility for:', department);
        const newVisible = new Set(visibleDepartments);
        if (newVisible.has(department)) {
            newVisible.delete(department);
            console.log('🔍 Hiding department:', department);
        } else {
            newVisible.add(department);
            console.log('🔍 Showing department:', department);
        }
        setVisibleDepartments(newVisible);
        console.log('🔍 New visible departments:', Array.from(newVisible));
    };

    // Filter employees by visible departments (if any are selected)
    const departmentFilteredEmployees = visibleDepartments.size === 0 
        ? sortedAndFilteredEmployees 
        : sortedAndFilteredEmployees.filter(emp => 
            visibleDepartments.has(getEmployeeRole(emp))
          );

    // Debug logging for filtering
    useEffect(() => {
        console.log('🔍 Filter Debug - Total employees:', employees.length);
        console.log('🔍 Filter Debug - Search filtered employees:', filteredEmployees.length);
        console.log('🔍 Filter Debug - Sorted and filtered employees:', sortedAndFilteredEmployees.length);
        console.log('🔍 Filter Debug - Final department filtered employees:', departmentFilteredEmployees.length);
        console.log('🔍 Filter Debug - Visible departments:', Array.from(visibleDepartments));
        console.log('🔍 Filter Debug - Sort order:', sortOrder);
    }, [employees.length, filteredEmployees.length, sortedAndFilteredEmployees.length, departmentFilteredEmployees.length, visibleDepartments, sortOrder]);

    // Add Manager Modal Handlers
    const handleAddManagerClick = () => {
        // Placeholder for the add manager click
    };

    const handleCloseAddManagerModal = () => {
        // Placeholder for the close add manager modal
    };

    const handleUserSearch = async (query) => {
        // Placeholder for the user search
    };

    const handleGrantManagerAccess = async (userId) => {
        // Placeholder for the grant manager access
    };

    // EventPopup handlers
    const handleNewTaskClick = () => {
        // Set default values for new task
        const now = moment();
        
        setSelectedDate(now.toDate());
        setSelectedStartTime(''); // Empty string so user can choose
        setSelectedEndTime(''); // Empty string so user can choose
        setSelectedEmployee(null); // No specific employee selected
        setExistingEvent(null);
        setPrefilledParticipants([]);
        setIsEventPopupOpen(true);
    };

    const handleEventSave = async (eventData) => {
        try {
            console.log('💾 Dashboard saving event:', eventData);
            
            // Use the existing handleScheduleCreate function
            await handleScheduleCreate(eventData);
            
            // Close the popup
            setIsEventPopupOpen(false);
            setExistingEvent(null);
            setPrefilledParticipants([]);
            setSelectedEmployee(null);
            
            // Don't call refreshSchedules() here as handleScheduleCreate already adds the event to state
        } catch (error) {
            console.error('Error saving event from Dashboard:', error);
            // You might want to show an error message to the user here
        }
    };

    const handleEventDelete = async (eventId) => {
        try {
            console.log('🗑️ Dashboard deleting event:', eventId);
            
            // Call the API to delete the event
            await api.delete(`/api/schedules/${eventId}`);
            
            // Remove the event from local state
            setSchedules(prev => prev.filter(schedule => schedule.id !== eventId));
            
            // Close the popup
            setIsEventPopupOpen(false);
            setExistingEvent(null);
            
            console.log('✅ Event deleted successfully');
        } catch (error) {
            console.error('Error deleting event from Dashboard:', error);
            // You might want to show an error message to the user here
        }
    };

    // Add handler for mode changes from HoverSidebar
    const handleModeChange = (mode) => {
        console.log('🔍 HoverSidebar - Mode change requested:', mode);
        console.log('🔍 HoverSidebar - Current selectedMode:', selectedMode);
        console.log('🔍 HoverSidebar - Current viewMode:', viewMode);
        
        setSelectedMode(mode);
        if (mode === 'calendar') {
            console.log('🔍 HoverSidebar - Setting viewMode to day');
            setViewMode('day');
        } else if (mode === 'scheduler') {
            console.log('🔍 HoverSidebar - Setting viewMode to week');
            setViewMode('week');
        }
        
        console.log('🔍 HoverSidebar - Mode change completed');
        // You can add more mode-specific logic here
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/login';
    };

    // Load theme from backend and apply it
    const loadThemeFromBackend = async () => {
        try {
            const response = await api.get('/api/users/employees');
            if (response.data?.employees) {
                // Find Alex Stretton in the employees list
                const userData = response.data.employees.find(emp => 
                    emp.first_name?.toLowerCase() === 'alex' && 
                    emp.last_name?.toLowerCase() === 'stretton'
                );
                
                if (userData && userData.theme_preference) {
                    const backendTheme = userData.theme_preference;
                    console.log('🎨 Manager Software - Loaded theme from backend:', backendTheme);
                    
                    // Update localStorage and apply theme
                    localStorage.setItem('theme', backendTheme);
                    
                    if (backendTheme === 'system') {
                        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                        document.documentElement.setAttribute('data-theme', systemTheme);
                    } else {
                        document.documentElement.setAttribute('data-theme', backendTheme);
                    }
                    
                    return backendTheme;
                }
            }
        } catch (error) {
            console.error('🎨 Manager Software - Failed to load theme from backend:', error);
        }
        
        return null;
    };

    // Apply theme on component mount and listen for theme changes
    useEffect(() => {
        const applyTheme = () => {
            const savedTheme = localStorage.getItem('theme') || 'dark';
            console.log('🎨 Manager Software - Applying theme:', savedTheme);
            
            if (savedTheme === 'system') {
                const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                console.log('🎨 Manager Software - System theme detected:', systemTheme);
                document.documentElement.setAttribute('data-theme', systemTheme);
            } else {
                console.log('🎨 Manager Software - Setting theme to:', savedTheme);
                document.documentElement.setAttribute('data-theme', savedTheme);
            }
        };

        // Load theme from backend first, then apply local theme as fallback
        const initializeTheme = async () => {
            const backendTheme = await loadThemeFromBackend();
            if (!backendTheme) {
                // Fallback to local theme if backend doesn't have one
                applyTheme();
            }
        };

        initializeTheme();

        // Poll for theme changes from backend every 3 seconds
        const themeCheckInterval = setInterval(async () => {
            const backendTheme = await loadThemeFromBackend();
            const currentLocalTheme = localStorage.getItem('theme');
            
            // If backend theme differs from local, update to backend theme
            if (backendTheme && backendTheme !== currentLocalTheme) {
                console.log('🎨 Manager Software - Backend theme changed, syncing:', backendTheme);
            }
        }, 3000);

        // Listen for system theme changes if system theme is selected
        const handleSystemThemeChange = () => {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme === 'system') {
                console.log('🎨 Manager Software - System theme change detected');
                applyTheme();
            }
        };

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', handleSystemThemeChange);

        return () => {
            clearInterval(themeCheckInterval);
            mediaQuery.removeEventListener('change', handleSystemThemeChange);
        };
    }, []);

    const styles = {
        container: {
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '8px',
            boxShadow: 'none',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            zIndex: 10,
            marginTop: '0',
        },
        mainContent: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            marginTop: '0',
            zIndex: 10,
        },
        timeHeaderWrapper: {
            backgroundColor: 'var(--bg-primary)',
            zIndex: 2,
            overflow: 'hidden',
            borderBottom: '1px solid var(--border-color)',
            width: '100%',
            display: 'flex',
            height: '70px',
            position: 'relative',
        },
        teamColumnSpace: {
            width: '280px',
            flexShrink: 0,
            backgroundColor: 'var(--bg-primary)',
            borderRight: '1px solid var(--border-color)',
            height: '70px',
            position: 'relative',
            zIndex: 3,
            overflow: 'hidden',
        },
        timeHeader: {
            display: 'flex',
            height: '70px',
            backgroundColor: 'var(--bg-primary)',
            width: viewMode === 'week' ? '100%' : '1700px',
            position: 'relative',
            transform: 'translateX(0)',
            transition: 'transform 0ms linear',
            flex: 1,
            overflow: 'hidden',
        },
        timeHeaderCell: {
            width: viewMode === 'week' ? 'calc(100% / 7)' : '100px',
            flexShrink: 0,
            borderRight: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-primary)',
            height: viewMode === 'week' ? '80px' : '70px',
            transition: 'all 0.2s ease',
            padding: '4px',
        },
        timeContentWrapper: {
            width: viewMode === 'week' ? '100%' : '70px',
            height: viewMode === 'week' ? '100%' : '70px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            padding: viewMode === 'week' ? '8px' : '4px',
        },
        timeNumber: {
            fontSize: '24px',
            fontWeight: '500',
            lineHeight: '1',
            marginBottom: '6px',
            textAlign: 'center',
            display: 'block',
            color: 'var(--text-primary)',
        },
        timePeriod: {
            fontSize: '14px',
            color: 'var(--text-secondary)',
            lineHeight: '1',
            textAlign: 'center',
            display: 'block',
        },
        employeeCell: {
            padding: '0 12px',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: 'var(--bg-primary)',
            height: viewMode === 'week' ? '180px' : '80px',
            minHeight: viewMode === 'week' ? '180px' : '80px',
            transition: 'all 0.2s ease',
            minWidth: '0',
        },
        employeeName: {
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        employeeRole: {
            fontSize: '12px',
            color: 'var(--text-secondary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        timeSlot: {
            width: viewMode === 'week' ? 'calc(100% / 7)' : '100px',
            flexShrink: 0,
            borderRight: '1px solid var(--border-color)',
            position: 'relative',
            backgroundColor: 'var(--bg-primary)',
            height: '100%',
            padding: viewMode === 'week' ? '8px' : '4px',
            display: viewMode === 'week' ? 'flex' : 'block',
            flexDirection: 'column',
            gap: '8px',
        },
        event: {
            position: viewMode === 'week' ? 'relative' : 'absolute',
            top: viewMode === 'week' ? 'auto' : '0',
            left: viewMode === 'week' ? 'auto' : '4px',
            right: viewMode === 'week' ? 'auto' : '4px',
            height: viewMode === 'week' ? '80%' : '100%',
            backgroundColor: '#E8F0FD',
            color: '#1760EB',
            border: '1px solid #1760EB',
            borderRadius: '6px',
            padding: viewMode === 'week' ? '8px 12px' : '4px 8px',
            fontSize: viewMode === 'week' ? '14px' : '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: viewMode === 'week' ? 'auto' : 'calc(100% - 8px)',
            boxShadow: viewMode === 'week' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
            margin: viewMode === 'week' ? '4px 0' : '0',
            zIndex: 1,
        },
        miniCalendar: {
            width: '300px',
            padding: '16px 16px 16px 24px',
            backgroundColor: 'transparent',
            borderRadius: '8px',
        },
        miniCalendarHeader: {
            marginBottom: '16px',
        },
        miniCalendarMonthYearHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
        },
        monthText: {
            fontSize: '32px',
            fontWeight: '600',
            color: 'var(--text-primary)',
        },
        yearText: {
            fontSize: '32px',
            color: '#EF4444',
            fontWeight: '600',
            marginLeft: '8px',
        },
        miniCalendarDayNames: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            textAlign: 'center',
            marginBottom: '0',
            paddingBottom: '0',
            borderBottom: 'none'
        },
        dayName: {
            color: '#666',
            fontSize: '12px',
            padding: '1px',
            fontWeight: '500',
        },
        miniCalendarDays: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginTop: '0',
            marginBottom: '16px',
        },
        calendarDay: {
            padding: '8px 4px',
            textAlign: 'center',
            fontSize: '14px',
            color: '#333',
            cursor: 'pointer',
            position: 'relative',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
        },
        calendarDayHover: {
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            transform: 'scale(1.05)',
        },
        today: {
            backgroundColor: '#EF4444',
            color: 'white',
            borderRadius: '6px',
        },
        otherMonth: {
            color: '#ccc',
        },
        miniCalendarButton: {
            border: 'none',
            background: 'none',
            padding: '8px',
            cursor: 'pointer',
            color: '#666',
            fontSize: '18px',
            '&:hover': {
                color: '#4263eb',
            },
        },
        loading: {
            textAlign: 'center',
            padding: '2rem',
            color: '#666',
        },
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close sort dropdown if clicking outside
            if (showSortOptions && !event.target.closest('.sort-dropdown-container')) {
                setShowSortOptions(false);
            }
            
            // Close filter dropdown if clicking outside
            if (showFilterOptions && !event.target.closest('.filter-dropdown-container')) {
                setShowFilterOptions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSortOptions, showFilterOptions]);

    if (loading) {
        return <div style={styles.loading}>Loading...</div>;
    }

    return (
        <div className="dashboard">
            <HoverSidebar
                selectedMode={selectedMode}
                onModeChange={handleModeChange}
                isManager={true}
            />
            
            <div className="dashboard-header">
                <div className="dashboard-title-row">
                    <div className="mini-calendar">
                        {renderMiniCalendar()}
                    </div>
                </div>
            </div>
            
            <div className="top-header">
                <h1 style={{ 
                    color: 'var(--text-primary)', 
                    fontFamily: 'Albert Sans, sans-serif',
                    fontWeight: '700',
                    fontSize: 'clamp(20px, 3vw, 32px)',
                    marginTop: '8px',
                    marginLeft: '12px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100vw - 800px)',
                    minWidth: '180px',
                    flex: 'none'
                }}>{companyInfo?.name}</h1>
                
                {/* Navigation Menu - Center */}
                <div style={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: '1',
                    minWidth: '300px'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: '24px'
                    }}>
                        <a 
                            href="#" 
                            className={`sub-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                            onClick={(event) => handleTabClick('dashboard', event)}
                        >
                            Dashboard
                        </a>
                        <a 
                            href="#" 
                            className={`sub-nav-item ${activeTab === 'employees' ? 'active' : ''}`}
                            onClick={(event) => handleTabClick('employees', event)}
                        >
                            Employees
                        </a>
                        <a 
                            href="#" 
                            className={`sub-nav-item ${activeTab === 'requests' ? 'active' : ''}`}
                            onClick={(event) => handleTabClick('requests', event)}
                        >
                            Requests
                        </a>
                    </div>
                </div>

                {/* Search Tasks - Right */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flex: '1',
                    minWidth: '200px',
                    paddingRight: '24px'
                }}>
                    <div className="task-search-box" style={{ 
                        width: 'clamp(180px, 15vw, 250px)',
                        display: 'flex',
                        alignItems: 'center',
                        background: 'var(--bg-secondary)',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                            <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M14 14L11 11" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input 
                            type="text" 
                            placeholder="Search Tasks" 
                            value={taskSearchTerm}
                            onChange={(e) => setTaskSearchTerm(e.target.value)}
                            style={{
                                border: 'none',
                                outline: 'none',
                                width: '100%',
                                background: 'transparent',
                                color: 'var(--text-primary)',
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif'
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="company-info">
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%', 
                    position: 'relative' 
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        marginLeft: '-10px'
                    }}>
                        {/* Day Navigation Controls */}
                        <div className="week-nav" style={{ marginRight: '12px' }}>
                            <button
                                className="nav-button"
                                onClick={goToPreviousDay}
                                title={viewMode === 'week' ? 'Previous week' : 'Previous day'}
                            >
                                ‹
                            </button>
                            <button
                                className="today-button"
                                onClick={goToToday}
                                title="Go to today"
                            >
                                Today
                            </button>
                            <button
                                className="nav-button"
                                onClick={goToNextDay}
                                title={viewMode === 'week' ? 'Next week' : 'Next day'}
                            >
                                ›
                            </button>
                        </div>

                        {/* New Task Button */}
                        <button 
                            className="new-task-button"
                            onClick={handleNewTaskClick}
                        >
                            New Task +
                        </button>

                        {/* Search Bar */}
                        <div style={{
                            width: '260px',
                            display: 'flex',
                            alignItems: 'center',
                            background: 'var(--bg-secondary)',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            border: '1px solid var(--border-color)',
                            marginRight: '-420px',
                            position: 'absolute',
                            right: 'max(1px, 45vw)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                        }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                                <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 14L11 11" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <input 
                                type="text" 
                                placeholder="Search for Team Members (e.g., Alex,Steve)" 
                                style={{
                                    border: 'none',
                                    outline: 'none',
                                    width: '100%',
                                    background: 'transparent',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    fontFamily: 'Inter, sans-serif'
                                }}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Day/Week Toggle Slider */}
                    <div className="view-mode-toggle">
                        <div className={`view-mode-toggle-slider ${viewMode}`}></div>
                        <div className="view-mode-toggle-content">
                            <div 
                                className={`view-mode-option ${viewMode === 'day' ? 'active' : 'inactive'}`}
                                onClick={() => setViewMode('day')}
                            >
                                Day
                            </div>
                            <div 
                                className={`view-mode-option ${viewMode === 'week' ? 'active' : 'inactive'}`}
                                onClick={() => setViewMode('week')}
                            >
                                Week
                            </div>
                        </div>
                    </div>

                    {/* Sort and Filter Buttons */}
                    <div className="sort-filter-container">
                        {/* Sort Button with Dropdown */}
                        <div className="sort-dropdown-container" style={{ position: 'relative' }}>
                            <button 
                                className="dropdown-button"
                                onClick={() => {
                                    console.log('🔍 Sort button clicked! Current showSortOptions:', showSortOptions);
                                    setShowSortOptions(!showSortOptions);
                                    console.log('🔍 Sort button - setting showSortOptions to:', !showSortOptions);
                                }}
                            >
                                Sort{sortOrder === 'highest' ? ' - Most Hours' : sortOrder === 'lowest' ? ' - Least Hours' : ''}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.01871 9.24934C4.21762 9.24934 4.40839 9.17032 4.54904 9.02967C4.68969 8.88902 4.76871 8.69825 4.76871 8.49934V2.55934L6.73871 4.52934C6.88088 4.66182 7.06893 4.73394 7.26323 4.73052C7.45753 4.72709 7.64292 4.64838 7.78033 4.51096C7.91774 4.37355 7.99646 4.18816 7.99988 3.99386C8.00331 3.79956 7.93119 3.61151 7.79871 3.46934L4.54871 0.21934C4.40808 0.0788896 4.21746 0 4.01871 0C3.81996 0 3.62933 0.0788896 3.48871 0.21934L0.238708 3.46934C0.165021 3.538 0.105919 3.6208 0.0649274 3.7128C0.0239354 3.8048 0.00189351 3.90411 0.000116722 4.00482C-0.00166006 4.10552 0.0168645 4.20555 0.0545855 4.29894C0.0923065 4.39233 0.148451 4.47716 0.21967 4.54838C0.290889 4.6196 0.375723 4.67574 0.469111 4.71346C0.562499 4.75118 0.662528 4.76971 0.763231 4.76793C0.863934 4.76615 0.963247 4.74411 1.05525 4.70312C1.14725 4.66213 1.23005 4.60303 1.29871 4.52934L3.26871 2.55934V8.49934C3.26871 8.91334 3.60471 9.24934 4.01871 9.24934Z" />
                                    </svg>
                                    <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'rotate(180deg)' }}>
                                        <path d="M4.01871 9.24934C4.21762 9.24934 4.40839 9.17032 4.54904 9.02967C4.68969 8.88902 4.76871 8.69825 4.76871 8.49934V2.55934L6.73871 4.52934C6.88088 4.66182 7.06893 4.73394 7.26323 4.73052C7.45753 4.72709 7.64292 4.64838 7.78033 4.51096C7.91774 4.37355 7.99646 4.18816 7.99988 3.99386C8.00331 3.79956 7.93119 3.61151 7.79871 3.46934L4.54871 0.21934C4.40808 0.0788896 4.21746 0 4.01871 0C3.81996 0 3.62933 0.0788896 3.48871 0.21934L0.238708 3.46934C0.165021 3.538 0.105919 3.6208 0.0649274 3.7128C0.0239354 3.8048 0.00189351 3.90411 0.000116722 4.00482C-0.00166006 4.10552 0.0168645 4.20555 0.0545855 4.29894C0.0923065 4.39233 0.148451 4.47716 0.21967 4.54838C0.290889 4.6196 0.375723 4.67574 0.469111 4.71346C0.562499 4.75118 0.662528 4.76971 0.763231 4.76793C0.863934 4.76615 0.963247 4.74411 1.05525 4.70312C1.14725 4.66213 1.23005 4.60303 1.29871 4.52934L3.26871 2.55934V8.49934C3.26871 8.91334 3.60471 9.24934 4.01871 9.24934Z" />
                                    </svg>
                                </div>
                            </button>

                            {/* Sort Dropdown Menu */}
                            {showSortOptions && (
                                <div 
                                    className="dropdown-menu" 
                                    style={{ 
                                        position: 'absolute',
                                        top: '100%',
                                        right: '0',
                                        zIndex: '99999',
                                        backgroundColor: 'var(--bg-secondary)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                        minWidth: '180px',
                                        marginTop: '4px'
                                    }}
                                >
                                    <div 
                                        className={`dropdown-item ${sortOrder === null ? 'active' : 'inactive'}`}
                                        onClick={() => {
                                            setSortOrder(null);
                                            setShowSortOptions(false);
                                            console.log('Sort: No sorting');
                                        }}
                                    >
                                        Default Order
                                    </div>
                                    <div 
                                        className={`dropdown-item ${sortOrder === 'highest' ? 'active' : 'inactive'}`}
                                        onClick={() => {
                                            setSortOrder('highest');
                                            setShowSortOptions(false);
                                            console.log('Sort: Most Hours First');
                                        }}
                                    >
                                        Most Hours First
                                    </div>
                                    <div 
                                        className={`dropdown-item ${sortOrder === 'lowest' ? 'active' : 'inactive'}`}
                                        onClick={() => {
                                            setSortOrder('lowest');
                                            setShowSortOptions(false);
                                            console.log('Sort: Least Hours First');
                                        }}
                                    >
                                        Least Hours First
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Filter Button */}
                        <div className="filter-dropdown-container" style={{ position: 'relative' }}>
                            <button 
                                className="dropdown-button"
                                onClick={() => {
                                    console.log('🔍 Filter button clicked! Current showFilterOptions:', showFilterOptions);
                                    setShowFilterOptions(!showFilterOptions);
                                    console.log('🔍 Filter button - setting showFilterOptions to:', !showFilterOptions);
                                }}
                            >
                                Filter{visibleDepartments.size > 0 ? ` (${visibleDepartments.size})` : ''}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 4.5C3 4.10218 3.15804 3.72064 3.43934 3.43934C3.72064 3.15804 4.10218 3 4.5 3H19.5C19.8978 3 20.2794 3.15804 20.5607 3.43934C20.842 3.72064 21 4.10218 21 4.5C21 4.89782 20.842 5.27936 20.5607 5.56066C20.2794 5.84196 19.8978 6 19.5 6H4.5C4.10218 6 3.72064 5.84196 3.43934 5.56066C3.15804 5.27936 3 4.89782 3 4.5Z" />
                                    <path d="M6 12C6 11.6022 6.15804 11.2206 6.43934 10.9393C6.72064 10.658 7.10218 10.5 7.5 10.5H16.5C16.8978 10.5 17.2794 10.658 17.5607 10.9393C17.842 11.2206 18 11.6022 18 12C18 12.3978 17.842 12.7794 17.5607 13.0607C17.2794 13.342 16.8978 13.5 16.5 13.5H7.5C7.10218 13.5 6.72064 13.342 6.43934 13.0607C6.15804 12.7794 6 12.3978 6 12Z" />
                                    <path d="M10.5 19.5C10.5 19.1022 10.658 18.7206 10.9393 18.4393C11.2206 18.158 11.6022 18 12 18C12.3978 18 12.7794 18.158 13.0607 18.4393C13.342 18.7206 13.5 19.1022 13.5 19.5C13.5 19.8978 13.342 20.2794 13.0607 20.5607C12.7794 20.842 12.3978 21 12 21C11.6022 21 11.2206 20.842 10.9393 20.5607C10.658 20.2794 10.5 19.8978 10.5 19.5Z" />
                                </svg>
                            </button>

                            {/* NEW SIMPLE FILTER DROPDOWN */}
                            {showFilterOptions && (
                                <div style={{
                                    position: 'absolute',
                                    top: '40px',
                                    right: '0px',
                                    width: '220px',
                                    backgroundColor: 'white',
                                    border: '2px solid #333',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                    zIndex: '999999',
                                    padding: '12px'
                                }}>
                                    {/* Header */}
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: '#666',
                                        marginBottom: '8px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Filter by Department
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                                        <button
                                            onClick={() => {
                                                const allDepts = new Set(['HR', 'employee', 'manager']);
                                                setVisibleDepartments(allDepts);
                                                console.log('🔍 Show All clicked');
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: '6px 12px',
                                                backgroundColor: '#007bff',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Show All
                                        </button>
                                        <button
                                            onClick={() => {
                                                setVisibleDepartments(new Set());
                                                console.log('🔍 Clear All clicked');
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: '6px 12px',
                                                backgroundColor: '#f8f9fa',
                                                color: '#333',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                    
                                    {/* Department List */}
                                    <div style={{ borderTop: '1px solid #eee', paddingTop: '8px' }}>
                                        {['HR', 'employee', 'manager'].map(dept => (
                                            <div 
                                                key={dept}
                                                onClick={() => {
                                                    console.log('🔍 Toggling department:', dept);
                                                    toggleDepartmentVisibility(dept);
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px',
                                                    cursor: 'pointer',
                                                    borderRadius: '4px',
                                                    backgroundColor: visibleDepartments.has(dept) ? '#e3f2fd' : 'transparent'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = visibleDepartments.has(dept) ? '#e3f2fd' : 'transparent'}
                                            >
                                                {/* Color Dot */}
                                                <div style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    backgroundColor: dept === 'HR' ? '#9C27B0' : '#4CAF50'
                                                }} />
                                                
                                                {/* Department Name */}
                                                <span style={{ 
                                                    flex: 1, 
                                                    fontSize: '14px',
                                                    color: '#333'
                                                }}>
                                                    {dept}
                                                </span>
                                                
                                                {/* Visibility Indicator */}
                                                <span style={{ 
                                                    fontSize: '16px',
                                                    color: visibleDepartments.has(dept) ? '#28a745' : '#dc3545'
                                                }}>
                                                    {visibleDepartments.has(dept) ? '✓' : '✗'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Status Info */}
                                    <div style={{
                                        borderTop: '1px solid #eee',
                                        paddingTop: '8px',
                                        marginTop: '8px',
                                        fontSize: '11px',
                                        color: '#666',
                                        textAlign: 'center'
                                    }}>
                                        {visibleDepartments.size === 0 ? 'Showing all employees' : `Showing ${visibleDepartments.size} departments`}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="dashboard-content">
                <div className="calendar-section">
                    <CustomCalendar
                        employees={departmentFilteredEmployees}
                        schedules={filteredSchedules}
                        setSchedules={setSchedules}
                        onScheduleCreate={handleScheduleCreate}
                        viewMode={viewMode}
                        refreshSchedules={refreshSchedules}
                        currentDate={currentDate}
                    />
                </div>
            </div>

            {/* Event Popup for New Task Creation */}
            <EventPopup
                isOpen={isEventPopupOpen}
                onClose={() => {
                    setIsEventPopupOpen(false);
                    setExistingEvent(null);
                    setPrefilledParticipants([]);
                    setSelectedEmployee(null);
                }}
                onSave={handleEventSave}
                onDelete={handleEventDelete}
                employee={selectedEmployee}
                startTime={selectedStartTime}
                endTime={selectedEndTime}
                date={selectedDate}
                existingEvent={existingEvent}
                prefilledParticipants={prefilledParticipants}
                employees={employees}
                isCreatingEvent={!existingEvent}
            />
        </div>
    );
};

export default Dashboard;